import Head from 'next/head';
import { getImageProps } from 'next/image';
import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  MetaTags,
  AssetsPrefetchLinks,
  FavIconLinks,
  TwitterTags,
  TwitterCard,
  OpenGraphTags,
} from '@eventbrite/webapp-public-content';

import {
  extractFacebookClickIds,
  useAttributionClickTracking,
  useTranscendConsent,
} from '@eventbrite/legacy-infra.clid-courier';
import { getExperimentAssignment } from '@eventbrite/legacy-infra.statsig';

import { Body } from '@components/Body';
import { Header } from '@components/Header';
import { getBaseDeepLinkAppUrl, JSONLDTags, SEOTags } from '@components/SEOTags';
import { TagManager } from '@/components/TagManager/TagManager';
import { GenericErrorPage } from '@/components/_shared/ErrorPages';
import { BasicInfoProvider } from '@/components/_shared/contexts/BasicInfoContext';
import { ExperimentsProvider } from '@/components/_shared/contexts/ExperimentsContext';
import { PreviewProvider } from '@/components/_shared/contexts/PreviewContext';
import { FeatureFlagsProvider } from '@/components/_shared/contexts';
import { DEFAULT_FEATURE_FLAGS } from '@/utils/featureFlags';

import { useEventDescription, useEventTitle } from '@/hooks';
import { addPagePropertiesFromRenderContext } from '@/utils/dataLayer';
import { sendEventViewTracking } from '@/utils/eventViewTracking';

import { onStatsigReady } from '../../../common/statsig';
import type { ListingPageProps } from '@/pages/listingPageTypes';
import { getUserDataQueryOptions } from '@/hooks/useUserData/useUserData';

const TWITTER_IOS_REFERRER = 'eiosurlxtcar';
const TWITTER_ANDROID_REFERRER = 'eandurlxtcar';

export type EventListingPageProps = ListingPageProps & {
  /** When set, renders <link rel="canonical" href={...} /> in Head (e.g. protected page points to gate URL). */
  canonicalHref?: string | null;
  /** OG/Twitter description. If not provided, uses basicInfo.isProtected ? '' : basicInfo.summary. */
  ogDescription?: string;
};

export function EventListingPage({
  context,
  locale,
  error,
  stage,
  googleMapsApiKey,
  ebHost,
  query,
  featureFlags = DEFAULT_FEATURE_FLAGS,
  experiments = {},
  globalHeaderProps,
  canonicalHref,
  ogDescription,
  isPreview,
}: EventListingPageProps): JSX.Element {
  const { data: userData } = useQuery(
    getUserDataQueryOptions(context?.basicInfo?.id, context?.basicInfo?.isOnline, !!context),
  );

  useEffect(() => {
    if (context && userData !== undefined) {
      const isAuthenticated = !!userData?.userId;
      addPagePropertiesFromRenderContext(context, isAuthenticated, !!isPreview);
    }
    if (context !== undefined && context != null && !isPreview) {
      sendEventViewTracking();
    }
  }, [context, userData, isPreview]);

  useEffect(() => {
    onStatsigReady(() => {
      Object.entries(experiments).forEach(([experimentName, experimentData]) => {
        const clientSideExperimentAssignment = getExperimentAssignment(experimentName);
        if (clientSideExperimentAssignment?.getGroupName() !== experimentData.groupName) {
          console.error('[Statsig Error] Experiment Assignment Mismatch!', {
            experimentName,
            serverSideGroupName: experimentData.groupName,
            clientSideGroupName: clientSideExperimentAssignment?.getGroupName(),
          });
        }
      });
    });
  }, [experiments]);

  const { sendClickData } = useAttributionClickTracking();
  const { isInitialized } = useTranscendConsent();

  useEffect(() => {
    if (isPreview || !isInitialized || !context) return;
    const clickIds = extractFacebookClickIds();
    if (clickIds.fbclid && context?.basicInfo?.organizationId) {
      sendClickData({
        organization_id: context.basicInfo.organizationId,
        is_server_side_enabled: true,
        ...clickIds,
      }).catch((error: unknown) => {
        console.warn('Failed to send click data to attribution service:', error);
      });
    }
  }, [context, isInitialized, isPreview]); // eslint-disable-line react-hooks/exhaustive-deps

  const eventDescription = useEventDescription(context?.basicInfo, locale);
  const eventTitle = useEventTitle(context?.basicInfo, locale);

  if (error) {
    return <GenericErrorPage error={error} stage={stage} />;
  }

  if (!context) {
    return <GenericErrorPage stage={stage} />;
  }

  const { basicInfo, collections, gallery, tags, structuredContent, seo, presenterInfo } = context;
  const agendaInfo = structuredContent?.widgets?.agenda;
  const primaryImageUrl = gallery?.images
    ?.map(
      ({ croppedLogoUrl480, croppedLogoUrl600, croppedLogoUrl940 }) =>
        croppedLogoUrl480 || croppedLogoUrl600 || croppedLogoUrl940,
    )
    .find(Boolean);

  const {
    props: { src },
  } = getImageProps({
    alt: '',
    width: 600,
    height: 300,
    src: primaryImageUrl || '',
  });

  const description = ogDescription ?? (basicInfo.isProtected ? '' : basicInfo.summary);

  return (
    <PreviewProvider isPreview={!!isPreview}>
      <Head>
        <title>{eventTitle}</title>
        <meta name="description" content={eventDescription} />
        {canonicalHref && <link rel="canonical" href={canonicalHref} />}
        <AssetsPrefetchLinks />
        <FavIconLinks stage={stage} />
      </Head>
      <MetaTags showDefaultDescription={false} />
      <OpenGraphTags
        url={basicInfo.url}
        title={basicInfo.name}
        description={description}
        imageUrl={src}
        type="events.event"
      />
      <TwitterTags
        description={description}
        title={basicInfo.name}
        imageUrl={src}
        iosAppUrl={getBaseDeepLinkAppUrl(basicInfo.id, query)(TWITTER_IOS_REFERRER)}
        androidAppUrl={getBaseDeepLinkAppUrl(basicInfo.id, query)(TWITTER_ANDROID_REFERRER)}
        twitterCard={TwitterCard.SummaryLargeImage}
      />
      <SEOTags ebHost={ebHost} basicInfo={basicInfo} query={query} seo={seo} locale={locale} isPreview={!!isPreview} />
      {!isPreview && (
        <JSONLDTags
          artistInfo={context?.artistInfo}
          basicInfo={basicInfo}
          gallery={gallery}
          primaryImageUrl={primaryImageUrl}
          salesStatus={context?.salesStatus}
          seo={seo}
          structuredContent={structuredContent}
          tags={tags}
        />
      )}

      <Header globalHeaderProps={globalHeaderProps} />
      <FeatureFlagsProvider flags={featureFlags}>
        <ExperimentsProvider experiments={experiments}>
          <BasicInfoProvider basicInfo={basicInfo}>
            <Body
              basicInfo={basicInfo}
              collections={collections}
              gallery={gallery}
              tags={tags}
              performers={context?.performers}
              structuredContent={structuredContent}
              artistInfo={context?.artistInfo}
              agendaInfo={agendaInfo}
              goodToKnow={context?.goodToKnow}
              googleMapsApiKey={googleMapsApiKey || ''}
              presenterInfo={presenterInfo}
              salesStatus={context?.salesStatus}
              stage={stage}
            />
          </BasicInfoProvider>
        </ExperimentsProvider>
      </FeatureFlagsProvider>

      {!isPreview && <TagManager publicUserId="" stage={stage} />}
    </PreviewProvider>
  );
}
